// The peak sheet's "View all on map": the hikes that summit a peak, opened in the explore map's
// hike pager (the web's cluster card with its "2 of 9" arrows), with the summit marked.
//
// Twins: Android `modules/explore/models/PeakSummitCarousel.kt`, iOS
// `Modules/Explore/Models/PeakSummitCarousel.swift`. The hikes come from the same
// `amenityById.hikesThatSummit` the peak sheet lists on every platform, in the server's order
// (most other peaks first) — not re-sorted.

import { gql } from 'graphql-request'

/** /explore?summit_peak_id=… — how any page asks the explore map for a peak's hikes. */
export const SUMMIT_PEAK_QUERY_PARAM = 'summit_peak_id'

/** The carousel's cap, as the apps' (`ExploreCluster.MAX_CARDS`) and the web cluster click's. */
export const MAX_CARDS = 50

/** The PostHog event the apps send too, and where it came from. */
export const PEAK_CAROUSEL_OPENED_EVENT = 'Peak Summit Carousel Opened'
export const SOURCE_POI_SHEET = 'poi_sheet'

/** How the routes the pager is not showing draw — thin, very faint route red. Same as the apps'
 *  `ExploreCarouselRouteStyle`; nothing else on the map changes while the pager is open. */
export const EXPLORE_CAROUSEL_ROUTE_STYLE = {
  otherRouteColor: '#DB2B35',
  otherRouteWidth: 3,
  otherRouteOpacity: 0.15
} as const

/** The summit a peak's pager was opened for. */
export interface PeakSummitOrigin {
  id: string
  name: string
  latitude: number | null
  longitude: number | null
  isBagged: boolean
}

export interface PeakSummitAmenity {
  id: string
  name: string | null
  latitude: number | null
  longitude: number | null
  bagged: boolean | null
  hikesThatSummit: Array<{ hike: { id: string } }> | null
}

export const PeakSummitHikes = gql`
  query ExplorePeakSummitHikes($id: ID!) {
    amenityById(id: $id) {
      id
      name
      latitude
      longitude
      bagged
      hikesThatSummit {
        hike { id }
      }
    }
  }
`

export const ExploreRoutes = gql`
  query ExploreRoutes($hikeIds: [ID!], $recordingIds: [ID!]) {
    exploreRoutes(hikeIds: $hikeIds, recordingIds: $recordingIds) {
      id
      itemType
      encodedPolyline
      precision
    }
  }
`

export function peakSummitOrigin (amenity: PeakSummitAmenity | null | undefined): PeakSummitOrigin | null {
  if (!amenity) return null
  return {
    id: amenity.id,
    name: amenity.name || '',
    latitude: amenity.latitude ?? null,
    longitude: amenity.longitude ?? null,
    isBagged: amenity.bagged === true
  }
}

/**
 * The peak's hikes in the shape the explore store reads a cluster leaf in: the official source
 * reads `properties.id`, the community source `item_type` / `item_id`, so both are filled and the
 * pager works whichever source the map is on. Duplicates dropped, capped, server order kept.
 */
export function peakSummitLeaves (amenity: PeakSummitAmenity | null | undefined): Array<{ properties: Record<string, unknown> }> {
  const seen = new Set<string>()
  const leaves: Array<{ properties: Record<string, unknown> }> = []
  for (const row of amenity?.hikesThatSummit || []) {
    const id = row?.hike?.id
    if (!id || seen.has(id) || Number.isNaN(Number(id))) continue
    seen.add(id)
    leaves.push({ properties: { id: Number(id), item_type: 'Hike', item_id: Number(id) } })
    if (leaves.length >= MAX_CARDS) break
  }
  return leaves
}

/** "Ben Nevis · 2 of 9" for a peak's pager; the plain counter for a cluster's. */
export function counterText (origin: PeakSummitOrigin | null | undefined, counter: string): string {
  const name = origin?.name?.trim()
  return name ? `${name} · ${counter}` : counter
}

/** The summit marker's GeoJSON, or null for a peak with no position. */
export function summitFeature (origin: PeakSummitOrigin | null | undefined): GeoJSON.Feature<GeoJSON.Point> | null {
  if (!origin || origin.latitude == null || origin.longitude == null) return null
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [origin.longitude, origin.latitude] },
    properties: { bagged: origin.isBagged }
  }
}

/** The summit id from /explore's query string, or null. */
export function summitPeakIdFromQuery (query: Record<string, unknown>): string | null {
  const raw = query[SUMMIT_PEAK_QUERY_PARAM]
  if (raw == null) return null
  const id = String(raw).trim()
  return id && /^\d+$/.test(id) ? id : null
}

/** Where "View all on map" goes from a page without the explore map. */
export function peakSummitExploreUrl (peakId: string): string {
  return `/explore?${SUMMIT_PEAK_QUERY_PARAM}=${encodeURIComponent(peakId)}`
}

/**
 * "View all on map": on /explore the map opens the pager in place; anywhere else the browser goes
 * to /explore, which opens it once its map is up.
 */
export function openPeakSummitHikesOnMap (peakId: string, win: Window = window): void {
  const inPlace = (win as unknown as { exploreShowPeakSummitHikes?: (id: string) => void }).exploreShowPeakSummitHikes
  if (typeof inPlace === 'function') {
    inPlace(peakId)
    return
  }
  win.location.assign(peakSummitExploreUrl(peakId))
}
